import React from 'react';
import { Query } from 'react-apollo';
import { TableRow, TableCell, Typography, IconButton } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import { GlobalCircleLoader, Pagination } from 'shared/components';
import Table, { header } from 'shared/components/Table';
import { UPLOAD_STATUS_QUERY } from 'upload-status/queries';
import { ListQueryResult } from 'upload-status/types';
import { SearchBarQueryVars } from './SearchBar';

interface Props {
  variables: SearchBarQueryVars;
  selectedStatuses: string[];
  queryKey: string;
  onRefresh: () => void;
}

interface State {
  page: number;
  perPage: number;
  cursors: (string | null)[];
}

const headers = [
  header('Trint ID'),
  header('Status'),
  header('Sub State'),
  header('Filename'),
  header('Operation Token'),
  header('Created'),
  header('Error Code'),
];

const colWidths = ['15%', '10%', '12%', '14%', '15%', '18%', '16%'];

const statusRowColors: Record<string, string> = {
  failed: 'rgba(244, 67, 54, 0.04)',
};

class UploadLogsTable extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      page: 0,
      perPage: 25,
      cursors: [null],
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.queryKey !== this.props.queryKey || prevProps.selectedStatuses !== this.props.selectedStatuses) {
      this.setState({ page: 0, cursors: [null] });
    }
  }

  handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    this.setState({ page: newPage });
  };

  handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({ perPage: Number(event.target.value), page: 0, cursors: [null] });
  };

  render() {
    const { variables, selectedStatuses, queryKey, onRefresh } = this.props;
    const { page, perPage, cursors } = this.state;

    const queryVars = {
      ...variables,
      ...(selectedStatuses.length > 0 && { status: selectedStatuses }),
      limit: perPage,
      ...(cursors[page] && { cursor: cursors[page] }),
    };

    const title =
      selectedStatuses.length > 0
        ? `${selectedStatuses
            .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
            .join(', ')} Uploads`
        : 'All Uploads';

    return (
      <Query<ListQueryResult>
        key={queryKey}
        query={UPLOAD_STATUS_QUERY}
        variables={queryVars}
        fetchPolicy="cache-and-network"
        onCompleted={(data: ListQueryResult) => {
          const { nextCursor } = data.uploadStatus.pagination;
          if (nextCursor && !cursors[page + 1]) {
            this.setState((prev) => {
              const next = [...prev.cursors];
              next[page + 1] = nextCursor;
              return { cursors: next };
            });
          }
        }}
      >
        {({ data, error, loading }) => {
          if (!data && loading) return <GlobalCircleLoader show />;
          if (error)
            return <pre style={{ color: 'red' }}>{error.message}</pre>;
          if (!data) return null;

          const { uploads, pagination } = data.uploadStatus;

          return (
            <div style={{ opacity: loading ? 0.6 : 1 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                <Typography variant="h6">{title}</Typography>
                <IconButton onClick={onRefresh} disabled={loading} aria-label="Refresh">
                  <RefreshIcon />
                </IconButton>
              </div>
              {uploads.length === 0 && page === 0 ? (
                <p>No uploads found.</p>
              ) : (
                <>
                  <Table headers={headers} colWidths={colWidths}>
                    {uploads.map((upload) => (
                      <TableRow
                        key={upload.operationToken}
                        style={{
                          backgroundColor:
                            statusRowColors[upload.status] || 'transparent',
                        }}
                      >
                        <TableCell style={{ wordBreak: 'break-all' }}>
                          {upload.trintId}
                        </TableCell>
                        <TableCell>{upload.status}</TableCell>
                        <TableCell>{upload.subState}</TableCell>
                        <TableCell>{upload.filename}</TableCell>
                        <TableCell style={{ wordBreak: 'break-all' }}>
                          {upload.operationToken}
                        </TableCell>
                        <TableCell>{upload.createdAt}</TableCell>
                        <TableCell>{upload.errorCode}</TableCell>
                      </TableRow>
                    ))}
                  </Table>
                  <Pagination
                    page={page}
                    perPage={perPage}
                    hasMore={pagination.hasMore}
                    onChangePage={this.handleChangePage}
                    onChangeRowsPerPage={this.handleChangeRowsPerPage}
                  />
                </>
              )}
            </div>
          );
        }}
      </Query>
    );
  }
}

export default UploadLogsTable;
